import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { playerService } from '../../services/playerService';
import EntityFinancialRecordsPanel from '../../components/finance/EntityFinancialRecordsPanel';

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon name={icon} size={16} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-foreground font-medium break-words">{value ?? 'N/A'}</p>
    </div>
  </div>
);

const formatPosition = (position) =>
  position ? position.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) : 'N/A';

const formatDate = (isoDate) => {
  if (!isoDate) return 'N/A';
  return new Date(isoDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-success/10 text-success';
  if (status === 'inactive' || status === 'suspended') return 'bg-muted text-muted-foreground';
  return 'bg-warning/10 text-warning';
};

const PlayerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPlayer = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await playerService.getById(id);
      setPlayer(data);
      document.title = `${data?.userProfile?.fullName || 'Player'} - Tanzania Basketball Federation`;
    } catch (err) {
      setError(err.message || 'Failed to load player');
    } finally {
      setIsLoading(false);
    }
  };

  const statistics = (player?.statistics || []).filter((stat) => stat?.gamesPlayed);

  return (
    <PublicBrowseShell>
      <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={() => navigate('/browse-players')} className="mb-6">
        Back to Players
      </Button>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading player...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : !player ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="User" size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Player not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border card-shadow p-6 flex items-center gap-6 flex-wrap">
            <div className="w-24 h-24 rounded-full bg-emerald-600/10 flex items-center justify-center overflow-hidden shrink-0">
              {player?.photoUrl ? (
                <Image src={player.photoUrl} alt={player?.userProfile?.fullName} className="w-full h-full object-cover" />
              ) : (
                <Icon name="User" size={40} className="text-emerald-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">{player?.userProfile?.fullName || 'Unknown Player'}</h1>
              <p className="text-muted-foreground">{player?.team?.name || 'Unassigned'}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary">
                  {formatPosition(player?.playerPosition)}
                </span>
                {player?.jerseyNumber != null && (
                  <span className="text-xs px-3 py-1 rounded-full bg-accent/10 text-accent-foreground">
                    #{player.jerseyNumber}
                  </span>
                )}
                <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusBadgeClass(player?.playerStatus)}`}>
                  {player?.playerStatus || 'pending'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Player Bio</h2>
              <div className="space-y-4">
                <InfoRow icon="Cake" label="Date of Birth" value={formatDate(player?.dateOfBirth)} />
                <InfoRow icon="Flag" label="Nationality" value={player?.nationality} />
                <InfoRow icon="Ruler" label="Height" value={player?.heightCm ? `${player.heightCm} cm` : null} />
                <InfoRow icon="Weight" label="Weight" value={player?.weightKg ? `${player.weightKg} kg` : null} />
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Team</h2>
              <div className="space-y-4">
                <InfoRow icon="Shield" label="Club" value={player?.team?.name} />
                <InfoRow icon="Layers" label="Category" value={player?.team?.category} />
                <InfoRow icon="Mail" label="Contact" value={player?.userProfile?.email} />
              </div>
            </div>
          </div>

          <EntityFinancialRecordsPanel
            entityType="player"
            entityId={player?.id}
            entityName={player?.userProfile?.fullName}
          />

          {statistics.length > 0 && (
            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Season Statistics</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4">League</th>
                      <th className="py-2 pr-4">GP</th>
                      <th className="py-2 pr-4">PTS</th>
                      <th className="py-2 pr-4">REB</th>
                      <th className="py-2 pr-4">AST</th>
                      <th className="py-2 pr-4">STL</th>
                      <th className="py-2 pr-4">BLK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statistics.map((stat) => (
                      <tr key={stat.id} className="border-b border-border last:border-0">
                        <td className="py-2 pr-4 text-foreground font-medium">
                          {stat?.league?.name || 'N/A'} {stat?.league?.season ? `(${stat.league.season})` : ''}
                        </td>
                        <td className="py-2 pr-4 text-foreground">{stat?.gamesPlayed ?? 0}</td>
                        <td className="py-2 pr-4 text-foreground">{stat?.points ?? 0}</td>
                        <td className="py-2 pr-4 text-foreground">{stat?.rebounds ?? 0}</td>
                        <td className="py-2 pr-4 text-foreground">{stat?.assists ?? 0}</td>
                        <td className="py-2 pr-4 text-foreground">{stat?.steals ?? 0}</td>
                        <td className="py-2 pr-4 text-foreground">{stat?.blocks ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default PlayerDetailPage;
